import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { ms } from "date-fns/locale";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { AlarmClock, CalendarClock, CheckCircle2, PenLine, Users, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import QueueItem from "@/components/QueueItem";
import HelperUpdateBanner from "@/components/HelperUpdateBanner";
import BgStatusCard from "@/components/BgStatusCard";
import api from "@/lib/api";

function Stat({ icon: Icon, label, value, accent, id, delay }) {
  return (
    <div className="panel fade-up p-5" style={{ animationDelay: `${delay}ms` }} data-testid={id}>
      <div className="flex items-center justify-between">
        <p className="eyebrow">{label}</p>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <p className="mt-4 font-mono text-4xl font-medium tracking-tight" data-testid={`${id}-value`}>{value ?? "–"}</p>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [due, setDue] = useState([]);

  const load = useCallback(async () => {
    const [s, q] = await Promise.all([
      api.get("/stats", { params: { tz_offset: new Date().getTimezoneOffset() } }),
      api.get("/queue", { params: { bucket: "due" } }),
    ]);
    setStats(s.data);
    setDue(q.data);
  }, []);

  useEffect(() => {
    load().catch(() => {});
    const t = setInterval(() => load().catch(() => {}), 30000);
    return () => clearInterval(t);
  }, [load]);

  const chart = (stats?.daily || []).map((d) => ({ ...d, label: format(new Date(d.date), "EEE", { locale: ms }) }));

  return (
    <div>
      <PageHeader
        eyebrow="Papan Pemuka"
        title="Selamat kembali"
        desc="Ringkasan post anda hari ini. Post group yang tiba masanya muncul di bawah — satu klik untuk salin & buka group."
        action={<Button asChild data-testid="dash-create-post-btn"><Link to="/tulis"><PenLine className="mr-2 h-4 w-4" /> Tulis Post</Link></Button>}
      />
      <HelperUpdateBanner />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={AlarmClock} label="Perlu dipost" value={stats?.due} accent="text-amber-400" id="dash-stat-due" delay={0} />
        <Stat icon={CalendarClock} label="Akan datang" value={stats?.upcoming} accent="text-primary" id="dash-stat-scheduled" delay={60} />
        <Stat icon={CheckCircle2} label="Selesai" value={stats?.done} accent="text-emerald-400" id="dash-stat-completed" delay={120} />
        <Stat icon={Users} label="Kumpulan" value={stats?.groups} accent="text-cyan-400" id="dash-stat-groups" delay={180} />
      </div>
      {stats?.awaiting > 0 && (
        <Link to="/jadual" state={{ bucket: "approval" }} className="fade-up mt-6 flex items-center justify-between rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-3 text-sm text-amber-200 transition-colors hover:bg-amber-500/15" data-testid="dash-awaiting-banner">
          <span><b className="font-mono">{stats.awaiting}</b> post sedang menunggu kelulusan admin group</span>
          <span className="font-semibold">Semak →</span>
        </Link>
      )}
      <BgStatusCard />

      <div className="mt-10 grid gap-8 lg:grid-cols-[1.6fr_1fr]">
        <section className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-bold">Perlu dipost sekarang</h2>
            <div className="flex flex-wrap items-center gap-4">
              {due.some((d) => d.kind === "group") && <Link to="/post-pantas" className="flex items-center gap-1 text-sm font-semibold text-emerald-400 hover:underline" data-testid="dash-fast-post-link"><Zap className="h-3.5 w-3.5" /> Mod Post Pantas</Link>}
              <Link to="/jadual" className="text-sm text-primary hover:underline" data-testid="dash-view-queue-link">Lihat semua</Link>
            </div>
          </div>
          <div className="space-y-4" data-testid="dash-due-list">
            {due.length === 0 && (
              <div className="panel p-10 text-center text-sm text-muted-foreground" data-testid="dash-due-empty">Tiada post tertunggak. Semua beres!</div>
            )}
            {due.slice(0, 4).map((item, i) => <QueueItem key={item.tid} item={item} due index={i} onChange={load} />)}
          </div>
        </section>
        <section className="panel h-fit min-w-0 p-5" data-testid="dash-weekly-chart">
          <p className="eyebrow">7 hari lepas</p>
          <h2 className="mt-1 text-lg font-bold">Post selesai</h2>
          <div className="mt-6 h-52 min-h-[208px]">
            <ResponsiveContainer width="100%" height={208}>
              <BarChart data={chart}>
                <XAxis dataKey="label" stroke="hsl(215 20% 55%)" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: "hsl(217 33% 18%)" }} contentStyle={{ background: "hsl(217 33% 13%)", border: "1px solid hsl(215 25% 22%)", borderRadius: 8 }} formatter={(v) => [v, "Post"]} />
                <Bar dataKey="count" fill="hsl(221 83% 53%)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>
    </div>
  );
}
